import api from './api';

// ===============================
// NEW BILLING SYSTEM INTERFACES
// ===============================

// Billing Period Management
export interface BillingPeriod {
  id: number;
  name: string;
  description?: string;
  startDate: string;
  endDate: string;
  status: BillingPeriodStatus;
  createdBy: number;
  createdAt: string;
  billsGeneratedAt?: string;
  billsGeneratedBy?: number;
  updatedAt: string;
}

export enum BillingPeriodStatus {
  Active = 0,
  Closed = 1,
  Billed = 2,
  Completed = 3
}

export interface CreateBillingPeriodRequest {
  name: string;
  description?: string;
  startDate: string;
  endDate: string;
}

export interface UpdateBillingPeriodRequest {
  name?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
}

// Completed Work Billing
export interface CompletedWorkBilling {
  id: number;
  shiftId: number;
  staffId: number;
  staffName: string;
  employeeId: string;
  clientId: number;
  clientName: string;
  shiftDate: string;
  actualStartTime: string;
  actualEndTime: string;
  totalHours: number;
  regularHours: number;
  overtimeHours: number;
  hourlyRate: number;
  overtimeRate?: number;
  totalAmount: number;
  approvedBy: number;
  approvedAt: string;
  approvalNotes?: string;
  workNotes?: string;
  isBilled: boolean;
  billingPeriodId?: number;
  billId?: number;
  billedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Bill Management
export interface Bill {
  id: number;
  billNumber: string;
  billingPeriodId: number;
  staffId: number;
  staffName: string;
  employeeId: string;
  billDate: string;
  periodStart: string;
  periodEnd: string;
  totalRegularHours: number;
  totalOvertimeHours: number;
  totalHours: number;
  regularAmount: number;
  overtimeAmount: number;
  grossAmount: number;
  totalDeductions: number;
  netAmount: number;
  status: BillStatus;
  totalShifts: number;
  notes?: string;
  generatedBy: number;
  generatedAt: string;
  createdAt: string;
  updatedAt: string;
  lineItems?: BillLineItem[];
  payroll?: Payroll;
}

export enum BillStatus {
  Draft = 0,
  Generated = 1,
  Reviewed = 2,
  Approved = 3,
  Paid = 4
}

export interface BillLineItem {
  id: number;
  billId: number;
  completedWorkBillingId: number;
  lineNumber: number;
  description: string;
  workDate: string;
  clientName: string;
  startTime: string;
  endTime: string;
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
  regularRate: number;
  overtimeRate?: number;
  regularAmount: number;
  overtimeAmount: number;
  totalAmount: number;
  notes?: string;
  createdAt: string;
}

// Payroll Management
export interface Payroll {
  id: number;
  payrollNumber: string;
  billingPeriodId: number;
  billId: number;
  staffId: number;
  staffName: string;
  employeeId: string;
  grossAmount: number;
  totalDeductions: number;
  netAmount: number;
  status: PayrollStatus;
  paymentMethod?: string;
  paymentReference?: string;
  createdAt: string;
  processedAt?: string;
  processedBy?: number;
  scheduledPaymentDate?: string;
  actualPaymentDate?: string;
  notes?: string;
  transactionId?: string;
  updatedAt: string;
}

export enum PayrollStatus {
  Pending = 0,
  Scheduled = 1,
  Processing = 2,
  Completed = 3,
  Failed = 4,
  Cancelled = 5
}

export interface ProcessPaymentRequest {
  paymentMethod: string;
  paymentReference?: string;
  scheduledPaymentDate?: string;
  notes?: string;
}

// API Response wrapper
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
  errors?: string[];
}

// Paginated response for billing periods
export interface PaginatedBillingPeriodsResponse {
  billingPeriods: BillingPeriod[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ===============================
// BILLING PERIOD SERVICES
// ===============================

export const billingPeriodService = {
  // Get all billing periods
  async getBillingPeriods(): Promise<ApiResponse<PaginatedBillingPeriodsResponse>> {
    try {
      const response = await api.get('/billing/BillingPeriods');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch billing periods');
    }
  },

  // Get active billing period
  async getActiveBillingPeriod(): Promise<ApiResponse<BillingPeriod | null>> {
    try {
      const response = await api.get('/billing/BillingPeriods/active');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch active billing period');
    }
  },

  // Get billing period by ID
  async getBillingPeriod(id: number): Promise<ApiResponse<BillingPeriod>> {
    try {
      const response = await api.get(`/billing/BillingPeriods/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch billing period');
    }
  },

  // Create new billing period
  async createBillingPeriod(request: CreateBillingPeriodRequest): Promise<ApiResponse<BillingPeriod>> {
    try {
      const response = await api.post('/billing/BillingPeriods', request);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create billing period');
    }
  },

  // Update billing period
  async updateBillingPeriod(id: number, request: UpdateBillingPeriodRequest): Promise<ApiResponse<BillingPeriod>> {
    try {
      const response = await api.put(`/billing/BillingPeriods/${id}`, request);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update billing period');
    }
  },

  // Close billing period
  async closeBillingPeriod(id: number): Promise<ApiResponse<BillingPeriod>> {
    try {
      const response = await api.post(`/billing/BillingPeriods/${id}/close`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to close billing period');
    }
  },

  // Complete billing period
  async completeBillingPeriod(id: number): Promise<ApiResponse<BillingPeriod>> {
    try {
      const response = await api.post(`/billing/BillingPeriods/${id}/complete`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to complete billing period');
    }
  }
};

// ===============================
// COMPLETED WORK SERVICES
// ===============================

export const completedWorkService = {
  // Get completed work for billing period
  async getCompletedWorkForBillingPeriod(billingPeriodId: number, includeUnbilled: boolean = true): Promise<ApiResponse<CompletedWorkBilling[]>> {
    try {
      const response = await api.get(`/billing/BillingPeriods/${billingPeriodId}/completed-work?includeUnbilled=${includeUnbilled}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch completed work');
    }
  },

  // Get billing period summary
  async getBillingPeriodSummary(billingPeriodId: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`/billing/BillingPeriods/${billingPeriodId}/summary`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch billing period summary');
    }
  },

  // Generate bills from completed work in billing period
  async generateBills(billingPeriodId: number): Promise<ApiResponse<Bill[]>> {
    try {
      const response = await api.post(`/billing/BillingPeriods/${billingPeriodId}/generate-bills`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to generate bills');
    }
  },

  // Get available completed work from roster API for billing period date range
  async getAvailableCompletedWork(billingPeriodId: number): Promise<ApiResponse<any[]>> {
    try {
      const response = await api.get(`/billing/BillingPeriods/${billingPeriodId}/available-completed-work`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch available completed work');
    }
  },

  // Import completed work from roster API into billing period
  async importCompletedWork(billingPeriodId: number): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/billing/BillingPeriods/${billingPeriodId}/import-completed-work`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to import completed work');
    }
  },

  // Update hours and/or rate on a completed work record (manual override)
  async updateCompletedWork(completedWorkId: number, updates: {
    totalHours?: number;
    hourlyRate?: number;
    workNotes?: string;
    recalculateSegments?: boolean;
  }): Promise<ApiResponse<CompletedWorkBilling>> {
    try {
      const response = await api.put(`/billing/BillingPeriods/completed-work/${completedWorkId}`, updates);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update completed work');
    }
  }
};

// ===============================
// BILL GENERATION SERVICES (using existing invoices endpoint)
// ===============================

export const billGenerationService = {
  // Generate bills for billing period (TODO: may need custom endpoint)
  async generateBillsForPeriod(billingPeriodId: number): Promise<ApiResponse<Bill[]>> {
    try {
      // Note: This endpoint may not exist yet, might need to be implemented
      const response = await api.post(`/billing/invoices/generate-for-period/${billingPeriodId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to generate bills');
    }
  },

  // Get bills for billing period (from billing period details)
  async getBillsForPeriod(billingPeriodId: number): Promise<ApiResponse<Bill[]>> {
    try {
      const response = await api.get(`/billing/BillingPeriods/${billingPeriodId}`);
      if (response.data.success && response.data.data && response.data.data.bills) {
        return {
          success: true,
          message: 'Bills retrieved successfully',
          data: response.data.data.bills,
          errors: null
        };
      }
      return {
        success: false,
        message: 'No bills found',
        data: [],
        errors: null
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch bills');
    }
  },

  // Get bill by ID with line items (from billing period details)
  async getBill(billId: number): Promise<ApiResponse<Bill>> {
    try {
      // Since we don't have a direct bill endpoint, we need to search through billing periods
      // This is not ideal, but works for now. In a real system, you'd have a Bills controller.
      const periodsResponse = await api.get('/billing/BillingPeriods');
      if (periodsResponse.data.success && periodsResponse.data.data && periodsResponse.data.data.billingPeriods) {
        for (const period of periodsResponse.data.data.billingPeriods) {
          const periodResponse = await api.get(`/billing/BillingPeriods/${period.id}`);
          if (periodResponse.data.success && periodResponse.data.data && periodResponse.data.data.bills) {
            const bill = periodResponse.data.data.bills.find((b: any) => b.id === billId);
            if (bill) {
              return {
                success: true,
                message: 'Bill retrieved successfully',
                data: bill,
                errors: null
              };
            }
          }
        }
      }
      throw new Error('Bill not found');
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch bill');
    }
  },

  // Update bill status
  async updateBillStatus(billId: number, status: BillStatus): Promise<ApiResponse<Bill>> {
    try {
      const response = await api.put(`/billing/bills/${billId}/status`, { status });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update bill status');
    }
  },

  // Download bill as PDF
  async downloadBillPdf(billId: number): Promise<Blob> {
    try {
      const response = await api.get(`/billing/BillingPeriods/bills/${billId}/pdf`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to download bill PDF');
    }
  },

  // Send bill invoice to employee via email
  async sendBillEmail(billId: number, recipientEmail: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`/billing/BillingPeriods/bills/${billId}/send-email`, { recipientEmail });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to send email');
    }
  }
};

// ===============================
// PAYROLL SERVICES
// ===============================

export const payrollService = {
  // Get payrolls for billing period (from billing period details)
  async getPayrollsForPeriod(billingPeriodId: number): Promise<ApiResponse<Payroll[]>> {
    try {
      const response = await api.get(`/billing/BillingPeriods/${billingPeriodId}`);
      if (response.data.success && response.data.data && response.data.data.payrolls) {
        return {
          success: true,
          message: 'Payrolls retrieved successfully',
          data: response.data.data.payrolls,
          errors: null
        };
      }
      return {
        success: false,
        message: 'No payrolls found',
        data: [],
        errors: null
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch payrolls');
    }
  },

  // Get payroll by ID
  async getPayroll(payrollId: number): Promise<ApiResponse<Payroll>> {
    try {
      const response = await api.get(`/billing/payroll/${payrollId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch payroll');
    }
  },

  // Generate payroll from completed work
  async generatePayroll(billingPeriodId: number): Promise<ApiResponse<Payroll[]>> {
    try {
      const response = await api.post('/billing/payroll/generate', { billingPeriodId });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to generate payroll');
    }
  },

  // Process payment for payroll
  async processPayment(payrollId: number, request: ProcessPaymentRequest): Promise<ApiResponse<Payroll>> {
    try {
      const response = await api.post(`/billing/payroll/${payrollId}/process-payment`, request);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to process payment');
    }
  },

  // Mark payment as completed
  async completePayment(payrollId: number, transactionId: string, actualPaymentDate?: string): Promise<ApiResponse<Payroll>> {
    try {
      const response = await api.post(`/billing/payroll/${payrollId}/process-payment`, {
        transactionId,
        actualPaymentDate: actualPaymentDate || new Date().toISOString()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to complete payment');
    }
  },

  // Get pending payrolls
  async getPendingPayrolls(): Promise<ApiResponse<Payroll[]>> {
    try {
      const response = await api.get('/billing/payroll/pending-admin-approvals');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch pending payrolls');
    }
  }
};

// ===============================
// BILLING RATE INTERFACES
// ===============================

export interface BillingRate {
  id: number;
  name: string;
  dayType: string; // "Weekday" | "Saturday" | "Sunday" | "PublicHoliday"
  timeStartMinutes: number;
  timeEndMinutes: number;
  hourlyRate: number;
  percentage: number;      // e.g. 100 = base, 103.29 = evening loading
  isBaseRate: boolean;     // true for the Weekday Daytime segment
  rateType: string; // "StaffPay" | "ClientCharge"
  isActive: boolean;
  effectiveFrom: string;
  effectiveTo?: string;
  /** NDIS Support Catalogue item code (e.g. "01_011_0107_1_1"). */
  supportItemNumber?: string;
  /** NDIS claim type (face-to-face, non face-to-face, provider travel, etc.). */
  claimType?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBillingRateRequest {
  name: string;
  dayType: string;
  timeStartMinutes: number;
  timeEndMinutes: number;
  hourlyRate: number;
  percentage?: number;
  isBaseRate?: boolean;
  isActive?: boolean;
  rateType: string;
  effectiveFrom: string;
  effectiveTo?: string;
  supportItemNumber?: string;
  claimType?: string;
}

export interface UpdateBillingRateRequest {
  name?: string;
  dayType?: string;
  timeStartMinutes?: number;
  timeEndMinutes?: number;
  hourlyRate?: number;
  percentage?: number;
  isBaseRate?: boolean;
  isActive?: boolean;
  rateType?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  supportItemNumber?: string;
  claimType?: string;
}

// ===============================
// STAFF BASE RATE INTERFACES
// ===============================

export interface StaffBaseRate {
  id: number;
  staffId: number;
  baseHourlyRate: number;
  effectiveFrom: string;
  effectiveTo?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SetStaffBaseRateRequest {
  staffId: number;
  baseHourlyRate: number;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface UpdateStaffBaseRateRequest {
  baseHourlyRate?: number;
  effectiveFrom?: string;
  effectiveTo?: string;
  isActive?: boolean;
}

// ===============================
// CLIENT BASE RATE INTERFACES
// ===============================

export type ServiceType = 'PersonalCare' | 'HomeHelp' | 'CommunityAccess';

export const SERVICE_TYPE_LABELS: Record<string, string> = {
  PersonalCare: 'Personal Care',
  HomeHelp: 'Home Help',
  CommunityAccess: 'Community Access',
};

export interface ClientBaseRate {
  id: number;
  clientId?: number;       // null = global default template
  serviceType: string;
  baseHourlyRate: number;
  effectiveFrom: string;
  effectiveTo?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SetClientBaseRateRequest {
  clientId?: number;
  serviceType: string;
  baseHourlyRate: number;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface UpdateClientBaseRateRequest {
  baseHourlyRate?: number;
  serviceType?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  isActive?: boolean;
}

// ===============================
// BILLING RATES SERVICE
// ===============================

// ===============================
// NDIS SUPPORT CATALOGUE SERVICE
// ===============================

export interface NdisSupportItem {
  id: number;
  serviceCategory: string;       // PersonalCare | HomeHelp | CommunityAccess | Sleepover | Transport
  dayType: string;               // Weekday | Saturday | Sunday | PublicHoliday | Any
  timeStartMinutes: number;
  timeEndMinutes: number;
  supportItemNumber: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NdisSupportItemRequest {
  serviceCategory: string;
  dayType: string;
  timeStartMinutes: number;
  timeEndMinutes: number;
  supportItemNumber: string;
  description?: string;
  isActive?: boolean;
}

export const ndisSupportItemService = {
  async getAll(serviceCategory?: string): Promise<ApiResponse<NdisSupportItem[]>> {
    const url = serviceCategory
      ? `/billing/NdisSupportItems?serviceCategory=${encodeURIComponent(serviceCategory)}`
      : '/billing/NdisSupportItems';
    const r = await api.get(url);
    return r.data;
  },

  async create(request: NdisSupportItemRequest): Promise<ApiResponse<NdisSupportItem>> {
    const r = await api.post('/billing/NdisSupportItems', request);
    return r.data;
  },

  async update(id: number, request: NdisSupportItemRequest): Promise<ApiResponse<NdisSupportItem>> {
    const r = await api.put(`/billing/NdisSupportItems/${id}`, request);
    return r.data;
  },

  async remove(id: number): Promise<ApiResponse<boolean>> {
    const r = await api.delete(`/billing/NdisSupportItems/${id}`);
    return r.data;
  },
};

export const billingRatesService = {
  async getAllRates(rateType?: string): Promise<ApiResponse<BillingRate[]>> {
    try {
      const url = rateType
        ? `/billing/BillingRates?rateType=${encodeURIComponent(rateType)}`
        : '/billing/BillingRates';
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch billing rates');
    }
  },

  async createRate(request: CreateBillingRateRequest): Promise<ApiResponse<BillingRate>> {
    try {
      const response = await api.post('/billing/BillingRates', request);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create billing rate');
    }
  },

  async updateRate(id: number, request: UpdateBillingRateRequest): Promise<ApiResponse<BillingRate>> {
    try {
      const response = await api.put(`/billing/BillingRates/${id}`, request);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update billing rate');
    }
  },

  async deleteRate(id: number): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(`/billing/BillingRates/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete billing rate');
    }
  }
};

// ===============================
// STAFF BASE RATE SERVICE
// ===============================

export const staffBaseRateService = {
  async getAll(): Promise<ApiResponse<StaffBaseRate[]>> {
    try {
      const response = await api.get('/billing/StaffBaseRates');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch staff base rates');
    }
  },

  async getActiveForStaff(staffId: number): Promise<ApiResponse<StaffBaseRate>> {
    try {
      const response = await api.get(`/billing/StaffBaseRates/staff/${staffId}/active`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch staff rate');
    }
  },

  async getHistoryForStaff(staffId: number): Promise<ApiResponse<{ history: StaffBaseRate[]; active: StaffBaseRate | null }>> {
    try {
      const response = await api.get(`/billing/StaffBaseRates/staff/${staffId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch staff rate history');
    }
  },

  async setRate(request: SetStaffBaseRateRequest): Promise<ApiResponse<StaffBaseRate>> {
    try {
      const response = await api.post('/billing/StaffBaseRates', request);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to set staff base rate');
    }
  },

  async updateRate(id: number, request: UpdateStaffBaseRateRequest): Promise<ApiResponse<StaffBaseRate>> {
    try {
      const response = await api.put(`/billing/StaffBaseRates/${id}`, request);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update staff base rate');
    }
  },

  async deleteRate(id: number): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(`/billing/StaffBaseRates/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete staff base rate');
    }
  }
};

// ===============================
// CLIENT BASE RATE SERVICE
// ===============================

export const clientBaseRateService = {
  async getAll(): Promise<ApiResponse<ClientBaseRate[]>> {
    try {
      const response = await api.get('/billing/ClientBaseRates');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch client base rates');
    }
  },

  async getDefaults(): Promise<ApiResponse<ClientBaseRate[]>> {
    try {
      const response = await api.get('/billing/ClientBaseRates/defaults');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch default client rates');
    }
  },

  async getForClient(clientId: number): Promise<ApiResponse<ClientBaseRate[]>> {
    try {
      const response = await api.get(`/billing/ClientBaseRates/client/${clientId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch client rates');
    }
  },

  async setRate(request: SetClientBaseRateRequest): Promise<ApiResponse<ClientBaseRate>> {
    try {
      const response = await api.post('/billing/ClientBaseRates', request);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to set client base rate');
    }
  },

  async updateRate(id: number, request: UpdateClientBaseRateRequest): Promise<ApiResponse<ClientBaseRate>> {
    try {
      const response = await api.put(`/billing/ClientBaseRates/${id}`, request);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update client base rate');
    }
  },

  async deleteRate(id: number): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(`/billing/ClientBaseRates/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete client base rate');
    }
  }
};

// ===============================
// CLIENT INVOICE GENERATION INTERFACES (shift-based)
// ===============================

export interface CompletedShiftForInvoice {
  shiftId: number;                // Roster ShiftId
  clientId: number;
  clientName: string;
  staffId: number;
  staffName: string;
  shiftDate: string;              // "yyyy-MM-dd"
  startDateTime: string;
  endDateTime: string;
  actualStartTime?: string;
  actualEndTime?: string;
  totalHours: number;
  hourlyRate: number;
  estimatedAmount: number;
  status: string;                 // "completed" | "approved"
  notes: string;
  isBilled: boolean;
  invoiceId?: number;
  invoiceNumber?: string;
  /**
   * Populated when the estimate could not be priced because the ClientCharge
   * rate configuration is missing or doesn't cover the shift's time window.
   * Display next to the $0 estimate so the user knows what to fix.
   */
  rateConfigError?: string | null;
  /** True when the shift is an inactive overnight (sleepover), billed flat per night. */
  isSleepover?: boolean;
  /** Travel kilometres recorded by staff at completion. */
  travelKilometres?: number;
}

export interface GenerateClientInvoiceRequest {
  clientId: number;
  clientName: string;
  shiftIds: number[];              // Roster shift IDs selected by the user
  serviceType: string;             // "PersonalCare" | "HomeHelp" | "CommunityAccess"
  periodStart: string;
  periodEnd: string;
  dueDate?: string;
  notes?: string;
  createdBy: string;
}

export interface ClientInvoiceLineItem {
  id: number;
  invoiceId: number;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  totalPrice: number;
  shiftId?: number;
  shiftDate?: string;
  startTime?: string;
  endTime?: string;
  staffId?: number;
  staffName?: string;
  category?: string;
}

export interface ClientInvoice {
  id: number;
  invoiceNumber: string;
  clientId: number;
  clientName: string;
  invoiceDate: string;
  dueDate: string;
  periodStart: string;
  periodEnd: string;
  subTotal: number;
  taxAmount: number;
  totalAmount: number;
  paidAmount: number;
  balanceAmount: number;
  status: number;
  statusText: string;
  notes?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  lineItems: ClientInvoiceLineItem[];
  /** @deprecated alias for lineItems */
  invoiceItems?: ClientInvoiceLineItem[];
}

export const INVOICE_STATUS_LABELS: Record<number, string> = {
  1: 'Draft',
  2: 'Pending',
  3: 'Sent',
  4: 'Paid',
  5: 'Overdue',
  6: 'Cancelled',
  7: 'Partially Paid',
};

export const SERVICE_TYPES = ['PersonalCare', 'HomeHelp', 'CommunityAccess'] as const;

// ===============================
// CLIENT INVOICE GENERATION SERVICE
// ===============================

export const clientInvoiceGenerationService = {
  async getCompletedShifts(
    clientId: number,
    startDate?: string,
    endDate?: string,
    includeBilled?: boolean
  ): Promise<ApiResponse<CompletedShiftForInvoice[]>> {
    try {
      const params = new URLSearchParams();
      params.append('clientId', clientId.toString());
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (includeBilled !== undefined) params.append('includeBilled', includeBilled.toString());
      const response = await api.get(`/billing/ClientInvoices/completed-shifts?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch completed shifts');
    }
  },

  async generateInvoice(request: GenerateClientInvoiceRequest): Promise<ApiResponse<ClientInvoice>> {
    try {
      const response = await api.post('/billing/ClientInvoices/generate', request);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to generate invoice');
    }
  },

  async getInvoices(clientId?: number): Promise<ApiResponse<ClientInvoice[]>> {
    try {
      const url = clientId !== undefined
        ? `/billing/ClientInvoices?clientId=${clientId}`
        : '/billing/ClientInvoices';
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch invoices');
    }
  },

  async getInvoiceById(id: number): Promise<ApiResponse<ClientInvoice>> {
    try {
      const response = await api.get(`/billing/ClientInvoices/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch invoice');
    }
  },

  async updateStatus(id: number, status: number): Promise<ApiResponse<ClientInvoice>> {
    try {
      const response = await api.put(`/billing/ClientInvoices/${id}/status`, { status });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update invoice status');
    }
  },

  async deleteInvoice(id: number): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(`/billing/ClientInvoices/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete invoice');
    }
  },

  async downloadPdf(id: number): Promise<Blob> {
    try {
      const response = await api.get(
        `/billing/BillingPeriods/client-invoices/${id}/pdf`,
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to download invoice PDF');
    }
  }
};

export interface InvoiceItem {
  id?: number;
  invoiceId?: number;
  description: string;
  shiftId?: number;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  taxAmount: number;
  serviceDate?: string;
}

export interface Payment {
  id?: number;
  invoiceId: number;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  paymentMethodText?: string;
  status: string;
  statusText?: string;
  transactionId?: string;
  referenceNumber?: string;
  notes?: string;
  bankName?: string;
  lastFourDigits?: string;
  createdAt?: string;
  updatedAt?: string;
  processedBy?: string;
}

export interface Invoice {
  id?: number;
  invoiceNumber?: string;
  clientId: number;
  clientName?: string;
  invoiceDate?: string;
  dueDate?: string;
  periodStart: string;
  periodEnd: string;
  subTotal?: number;
  taxAmount?: number;
  totalAmount?: number;
  paidAmount?: number;
  balanceAmount?: number;
  status?: string;
  statusText?: string;
  billingFrequency?: string;
  billingFrequencyText?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  invoiceItems?: InvoiceItem[];
  payments?: Payment[];
}

// ===============================
// LEGACY BILLING SERVICE (Keep for backward compatibility)
// ===============================

export const legacyBillingService = {
  // Legacy invoice methods...
  async getInvoices(filters?: any): Promise<ApiResponse<Invoice[]>> {
    try {
      const queryParams = new URLSearchParams();
      if (filters) {
        Object.keys(filters).forEach(key => {
          if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
            queryParams.append(key, filters[key].toString());
          }
        });
      }
      
      const url = queryParams.toString() ? `/billing/invoices?${queryParams.toString()}` : '/billing/invoices';
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch invoices');
    }
  }
};

// ===============================
// FLAT RATE INTERFACES (Sleepover, Transport)
// ===============================

export interface FlatRate {
  id: number;
  name: string;
  category: string;        // "Sleepover" | "Transport"
  rateType: string;        // "StaffPay" | "ClientCharge"
  ndisLineItem?: string;
  unitRate: number;
  unit: string;            // "per night" | "per kilometre" | etc.
  isActive: boolean;
  effectiveFrom: string;
  effectiveTo?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFlatRateRequest {
  name: string;
  category: string;
  rateType: string;
  ndisLineItem?: string;
  unitRate: number;
  unit: string;
  isActive?: boolean;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface UpdateFlatRateRequest {
  name?: string;
  category?: string;
  rateType?: string;
  ndisLineItem?: string;
  unitRate?: number;
  unit?: string;
  isActive?: boolean;
  effectiveFrom?: string;
  effectiveTo?: string;
}

export const FLAT_RATE_CATEGORIES = ['Sleepover', 'Transport'] as const;
export type FlatRateCategory = typeof FLAT_RATE_CATEGORIES[number];

export const FLAT_RATE_CATEGORY_LABELS: Record<string, string> = {
  Sleepover: 'Sleepover',
  Transport: 'Transport',
};

export const flatRateService = {
  async getAll(category?: string, rateType?: string): Promise<ApiResponse<FlatRate[]>> {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (rateType) params.append('rateType', rateType);
      const url = params.toString() ? `/billing/FlatRates?${params.toString()}` : '/billing/FlatRates';
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch flat rates');
    }
  },

  async create(request: CreateFlatRateRequest): Promise<ApiResponse<FlatRate>> {
    try {
      const response = await api.post('/billing/FlatRates', request);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create flat rate');
    }
  },

  async update(id: number, request: UpdateFlatRateRequest): Promise<ApiResponse<FlatRate>> {
    try {
      const response = await api.put(`/billing/FlatRates/${id}`, request);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update flat rate');
    }
  },

  async delete(id: number): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(`/billing/FlatRates/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete flat rate');
    }
  }
};

// ===============================
// BACKWARD COMPAT EXPORTS
// ===============================

/**
 * Backwards-compatible alias of the shift-based client invoice service.
 * Period-based invoice generation has been removed; create invoices by
 * selecting completed shifts from the Client Invoices page instead.
 */
export const clientInvoiceService = {
  ...clientInvoiceGenerationService,

  async downloadPdf(invoiceId: number): Promise<Blob> {
    try {
      const response = await api.get(
        `/billing/BillingPeriods/client-invoices/${invoiceId}/pdf`,
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to download invoice PDF');
    }
  },

  async markAsSent(invoiceId: number): Promise<ApiResponse<ClientInvoice>> {
    return clientInvoiceGenerationService.updateStatus(invoiceId, 3);
  },

  async markAsPaid(invoiceId: number): Promise<ApiResponse<ClientInvoice>> {
    return clientInvoiceGenerationService.updateStatus(invoiceId, 4);
  },
};

/** @deprecated Use INVOICE_STATUS_LABELS instead */
export enum InvoiceStatus {
  Draft = 1,
  Pending = 2,
  Sent = 3,
  Paid = 4,
  Overdue = 5,
  Cancelled = 6,
  PartiallyPaid = 7,
}

// Export default as the new billing service structure
export default {
  billingPeriod: billingPeriodService,
  completedWork: completedWorkService,
  billGeneration: billGenerationService,
  payroll: payrollService,
  legacy: legacyBillingService,
  clientInvoice: clientInvoiceGenerationService,
  billingRates: billingRatesService,
  staffBaseRate: staffBaseRateService,
  clientBaseRate: clientBaseRateService,
  flatRate: flatRateService
};